import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TEST_IDS } from "@/constants/testIds";
import { Heart, MessageCircle, Bookmark, Share2, Trophy, Crown, Medal, ImagePlus, X, Trash2, Archive, Flag } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";

const PLACEHOLDER_IMAGES = [
  "https://images.unsplash.com/photo-1605884690323-e653ba876cb6",
  "https://images.pexels.com/photos/35035634/pexels-photo-35035634.jpeg",
];

const RANK_META = {
  1: { Icon: Crown, accent: "bg-accent text-accent-foreground" },
  2: { Icon: Medal, accent: "bg-accent text-accent-foreground" },
  3: { Icon: Trophy, accent: "bg-muted text-foreground" },
};

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [filter, setFilter] = useState("all");
  const [caption, setCaption] = useState("");
  const [image, setImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [lightbox, setLightbox] = useState(null); // { src, caption, user }

  const load = async (f = filter) => {
    const { data } = await api.get(`/community/feed${f === "following" ? "?filter=following" : ""}`);
    setPosts(data);
  };

  useEffect(() => {
    load("all");
    api.get("/community/weekly-top").then(({ data }) => setWeekly(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilter = async (f) => {
    setFilter(f);
    await load(f);
  };

  const submit = async () => {
    if (!caption.trim()) return;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption);
      if (image) fd.append("image", image);
      const { data } = await api.post("/community/posts", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPosts((p) => [data, ...p]);
      setCaption(""); setImage(null);
    } finally { setPosting(false); }
  };

  const like = async (id) => {
    const { data } = await api.post(`/community/posts/${id}/like`);
    setPosts((arr) => arr.map((p) => p.id === id ? { ...p,
      likes: data.liked ? [...(p.likes || []), user.user_id] : (p.likes || []).filter((u) => u !== user.user_id),
      liked_by_me: data.liked,
    } : p));
  };

  const save = async (id) => {
    const { data } = await api.post(`/community/posts/${id}/save`);
    setPosts((arr) => arr.map((p) => p.id === id ? { ...p, saved_by_me: data.saved } : p));
    toast.success(data.saved ? "Saved" : "Removed from saved");
  };

  const share = (post) => {
    const url = `${window.location.origin}/u/${post.username || post.user_id}`;
    if (navigator.share) {
      navigator.share({ title: "Stylebay — a look I love", text: post.caption, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/community/posts/${id}`);
      setPosts((arr) => arr.filter((p) => p.id !== id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const archivePost = async (id) => {
    if (!window.confirm("Move to Look Book → Social? The public post will be removed.")) return;
    try {
      await api.post(`/community/posts/${id}/archive`);
      setPosts((arr) => arr.filter((p) => p.id !== id));
      toast.success("Moved to Look Book → Social");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive");
    }
  };

  const reportPost = async (id) => {
    // Simple reason prompt — full modal would be nicer but this is a
    // low-frequency action; a browser prompt is fine + accessible.
    const reason = window.prompt(
      "Why are you reporting this post?\n\nspam / harassment / nudity / copyright / other",
      "spam"
    );
    if (!reason) return;
    try {
      const { data } = await api.post(`/community/posts/${id}/report`, {
        reason: reason.trim().toLowerCase(),
      });
      if (data.already_reported) {
        toast.info("You've already reported this post — thanks.");
      } else {
        toast.success("Reported. Our team will review shortly.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not report");
    }
  };

  const comment = async (id) => {
    const text = commentDrafts[id];
    if (!text?.trim()) return;
    const { data } = await api.post(`/community/posts/${id}/comment`, { text });
    setPosts((arr) => arr.map((p) => p.id === id ? { ...p, comments: [...(p.comments || []), data] } : p));
    setCommentDrafts((d) => ({ ...d, [id]: "" }));
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="eyebrow">Social · Looks of the week</p>
      <h1 className="editorial-h1 mt-3">The atelier feed.</h1>

      {/* Weekly rewards — condensed status-only banner */}
      <section data-testid="weekly-rewards" className="mt-8 border border-border px-5 py-3 bg-accent/5 flex items-center gap-3 flex-wrap">
        <Trophy size={14} className="shrink-0" />
        <p className="text-[11px] uppercase tracking-[0.18em]">Weekly rewards</p>
        <p className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
          {weekly?.headline || "Top-liked outfits win this week's rewards."}
        </p>
        {(weekly?.winners || []).slice(0, 3).length > 0 && (
          <p className="text-[11px] text-accent">
            {weekly.winners.length} winner{weekly.winners.length > 1 ? "s" : ""} · leaderboard live
          </p>
        )}
      </section>

      {(weekly?.winners || []).length > 0 && (
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          {weekly.winners.map((w, i) => {
            const meta = RANK_META[i + 1];
            const RankIcon = meta?.Icon || Trophy;
            return (
              <div key={w.id} data-testid={`weekly-winner-${i + 1}`} className="border border-border bg-card overflow-hidden">
                <div className={`flex items-center gap-2 px-3 py-1.5 ${meta?.accent || "bg-muted"}`}>
                  <RankIcon size={13} />
                  <span className="text-[10px] uppercase tracking-[0.2em]">#{i + 1} · {(w.likes || []).length} likes</span>
                </div>
                {w.image_path && (
                  <img src={fileUrl(w.image_path)} alt="" className="w-full aspect-square object-cover" />
                )}
                <div className="p-2.5">
                  <p className="text-xs font-medium truncate">{w.user_name || "Anonymous"}</p>
                  <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{w.caption}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 border border-border p-4 bg-card" data-testid={TEST_IDS.community.composer}>
        <div className="flex items-center gap-3">
          <PhotoPicker
            onSelect={(files) => setImage(files[0] || null)}
            multiple={false}
            cameraFacing="environment"
            testId="community-picker"
          >
            <Button
              type="button"
              variant="outline"
              className="rounded-none uppercase tracking-[0.2em] text-[10px] h-9"
              data-testid="community-image-trigger"
            >
              <ImagePlus size={13} className="mr-1.5" />
              {image ? "Change" : "Photo"}
            </Button>
          </PhotoPicker>
          {image && (
            <div className="relative shrink-0">
              <img
                src={URL.createObjectURL(image)}
                alt="preview"
                className="h-9 w-9 object-cover border border-border"
              />
              <button
                type="button"
                onClick={() => setImage(null)}
                className="absolute -top-1.5 -right-1.5 bg-accent text-accent-foreground rounded-full p-0.5"
                aria-label="Remove image"
                data-testid="community-image-remove"
              >
                <X size={9} />
              </button>
            </div>
          )}
          <Input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Share today's look…"
            className="rounded-none flex-1 h-9 text-sm"
          />
          <Button
            data-testid={TEST_IDS.community.postBtn}
            onClick={submit}
            disabled={posting || !caption.trim()}
            className="rounded-none uppercase tracking-[0.2em] text-[10px] h-9"
          >
            {posting ? "…" : "Post"}
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mt-8 flex gap-2">
        {["all", "following"].map((f) => (
          <button
            key={f}
            data-testid={`community-filter-${f}`}
            onClick={() => onFilter(f)}
            className={`px-4 py-2 border text-[11px] uppercase tracking-[0.2em] ${
              filter === f ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
            }`}
          >{f === "all" ? "Discover" : "Following"}</button>
        ))}
      </div>

      <div className="mt-6 space-y-10">
        {posts.length === 0 && (
          <div className="border border-dashed border-border p-12 text-center">
            <p className="font-serif text-3xl">
              {filter === "following" ? "Follow some atelier members to see their looks here." : "Be the first to share a look."}
            </p>
          </div>
        )}
        {posts.map((p, idx) => {
          const imgSrc = p.image_path ? fileUrl(p.image_path) : PLACEHOLDER_IMAGES[idx % PLACEHOLDER_IMAGES.length];
          return (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-border bg-card"
            >
              <header className="p-4 flex items-center gap-3 border-b border-border">
                <Link
                  to={p.username ? `/@${p.username}` : `/u/${p.user_id}`}
                  data-testid={`community-author-avatar-${p.id}`}
                  className="h-9 w-9 border border-border overflow-hidden rounded-full"
                >
                  {p.user_picture
                    ? <img src={resolveMediaUrl(p.user_picture)} alt="" className="h-full w-full object-cover" />
                    : <span className="text-xs flex items-center justify-center h-full">{p.user_name?.[0] || "S"}</span>}
                </Link>
                <div className="flex-1">
                  <Link
                    to={p.username ? `/@${p.username}` : `/u/${p.user_id}`}
                    data-testid={`community-author-name-${p.id}`}
                    className="text-sm font-medium hover:underline"
                  >
                    {p.user_name || "Anonymous"}
                  </Link>
                  {p.username && (
                    <Link
                      to={`/@${p.username}`}
                      data-testid={`community-author-handle-${p.id}`}
                      className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:opacity-70"
                    >
                      @{p.username}
                    </Link>
                  )}
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</p>
                {user?.user_id && user.user_id === p.user_id ? (
                  <div className="flex items-center gap-1 ml-1" data-testid={`community-owner-actions-${p.id}`}>
                    <button
                      type="button"
                      onClick={() => archivePost(p.id)}
                      title="Move to Look Book · Social"
                      data-testid={`community-archive-${p.id}`}
                      className="h-8 w-8 inline-flex items-center justify-center hover:text-accent transition"
                    ><Archive size={14} /></button>
                    <button
                      type="button"
                      onClick={() => deletePost(p.id)}
                      title="Delete post"
                      data-testid={`community-delete-${p.id}`}
                      className="h-8 w-8 inline-flex items-center justify-center hover:text-destructive transition"
                    ><Trash2 size={14} /></button>
                  </div>
                ) : user?.user_id && (
                  // IT Act §79 intermediary safe-harbor — every user-
                  // generated post must have a reachable "Report" affordance.
                  <button
                    type="button"
                    onClick={() => reportPost(p.id)}
                    title="Report this post"
                    data-testid={`community-report-${p.id}`}
                    className="h-8 w-8 inline-flex items-center justify-center text-muted-foreground hover:text-destructive transition ml-1"
                  ><Flag size={14} /></button>
                )}
              </header>
              <button
                type="button"
                onClick={() => setLightbox({ src: imgSrc, caption: p.caption, user: p.user_name })}
                className="block w-full bg-muted"
                data-testid={`community-image-${p.id}`}
                aria-label="Open full image"
              >
                <img src={imgSrc} alt="" className="w-full h-auto object-contain max-h-[85vh] mx-auto" loading="lazy" />
              </button>
              <div className="p-4">
                <p className="text-sm">{p.caption}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      data-testid={TEST_IDS.community.likeBtn(p.id)}
                      onClick={() => like(p.id)}
                      className="flex items-center gap-1.5 text-sm hover:opacity-70 transition"
                    >
                      <Heart size={16} fill={p.liked_by_me ? "currentColor" : "none"} />
                      {(p.likes || []).length}
                    </button>
                    <span className="flex items-center gap-1.5 text-sm">
                      <MessageCircle size={16} /> {(p.comments || []).length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`community-save-${p.id}`} onClick={() => save(p.id)}
                            aria-label="Save" className="p-2 hover:opacity-70 transition">
                      <Bookmark size={16} fill={p.saved_by_me ? "currentColor" : "none"} />
                    </button>
                    <button data-testid={`community-share-${p.id}`} onClick={() => share(p)}
                            aria-label="Share" className="p-2 hover:opacity-70 transition">
                      <Share2 size={16} />
                    </button>
                  </div>
                </div>
                {(p.comments || []).slice(-3).map((c) => (
                  <div key={c.id} className="mt-3 text-sm">
                    <span className="font-medium">{c.user_name}</span>
                    <span className="ml-2 text-muted-foreground">{c.text}</span>
                  </div>
                ))}
                <div className="mt-3 flex gap-2">
                  <Input
                    value={commentDrafts[p.id] || ""}
                    onChange={(e) => setCommentDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && comment(p.id)}
                    placeholder="Add a comment…"
                    className="rounded-none h-9 text-sm"
                  />
                  <Button onClick={() => comment(p.id)} className="rounded-none h-9 uppercase tracking-[0.2em] text-[10px]">Send</Button>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      {/* Full-screen image lightbox — click backdrop or × to close */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="community-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-in fade-in"
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close"
            data-testid="community-lightbox-close"
            className="absolute top-4 right-4 text-white p-2 hover:opacity-70"
          >
            <X size={22} />
          </button>
          <div className="max-h-full max-w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt=""
              className="max-h-[85vh] max-w-full object-contain"
              style={{ touchAction: "pinch-zoom" }}
            />
            {(lightbox.user || lightbox.caption) && (
              <div className="text-white/85 text-center max-w-lg">
                {lightbox.user && <p className="text-xs uppercase tracking-[0.2em] opacity-70">{lightbox.user}</p>}
                {lightbox.caption && <p className="text-sm mt-1">{lightbox.caption}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
